import React, { useEffect, useState } from "react";
import { Trash2, Upload } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import SiteContentEditor from "../components/SiteContentEditor";

const Admin = () => {
  const { t } = useLang();
  const { user, loading, signIn } = useAuth();
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("content");
  const [breeders, setBreeders] = useState([]);
  const [listings, setListings] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [repForm, setRepForm] = useState({
    title: "", species: "Ball Python", morph: "", price: 0, sex: "male", weight_g: 200,
    hatch_date: "", genetics: "", hets: "", breeder_id: "", breeder_name: "",
    breeder_notes: "Ejemplar de élite producido bajo los estándares MONZAL.", cover: "", gallery: "", featured: false,
    age_months: 5, currency: "MXN",
  });

  const [prodForm, setProdForm] = useState({
    title: "", category: "accessories", description: "", price: 0, stock: 100, cover: "", featured: false, currency: "MXN",
  });

  useEffect(() => {
    if (!user?.is_admin) return;
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/breeders").then((r) => setBreeders(r.data));
    fetchListings();
  }, [user]);

  const fetchListings = async () => {
    const r = await api.get("/reptiles", { params: { limit: 60 } });
    setListings(r.data);
  };

  const handleSaveReptile = async () => {
    setSaving(true);
    try {
      const breeder = breeders.find((b) => b.id === repForm.breeder_id) || breeders[0];
      const payload = {
        ...repForm,
        breeder_id: breeder?.id || "",
        breeder_name: breeder?.name || repForm.breeder_name,
        price: parseFloat(repForm.price),
        weight_g: parseFloat(repForm.weight_g),
        age_months: parseFloat(repForm.age_months),
        genetics: repForm.genetics ? repForm.genetics.split(",").map((s) => s.trim()).filter(Boolean) : [],
        hets: repForm.hets ? repForm.hets.split(",").map((s) => s.trim()).filter(Boolean) : [],
        gallery: repForm.gallery ? repForm.gallery.split(",").map((s) => s.trim()).filter(Boolean) : [],
      };
      await api.post("/admin/reptiles", payload);
      toast.success("Reptile created");
      setRepForm({ ...repForm, title: "", morph: "", price: 0, cover: "", gallery: "" });
      fetchListings();
      const s = await api.get("/admin/stats");
      setStats(s.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveProduct = async () => {
    setSaving(true);
    try {
      const payload = {
        ...prodForm,
        price: parseFloat(prodForm.price),
        stock: parseInt(prodForm.stock),
        images: prodForm.cover ? [prodForm.cover] : [],
      };
      await api.post("/admin/products", payload);
      toast.success("Product created");
      setProdForm({ ...prodForm, title: "", description: "", price: 0, cover: "" });
    } catch (e) {
      toast.error("Error");
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (e, target) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`;
      if (target === "reptile") setRepForm({ ...repForm, cover: url });
      else setProdForm({ ...prodForm, cover: url });
      toast.success("Uploaded");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/admin/reptiles/${id}`);
    fetchListings();
    toast.success("Deleted");
  };

  const handleReseed = async () => {
    if (!window.confirm("Re-seed?")) return;
    await api.post("/seed?force=true");
    toast.success("Seeded");
    fetchListings();
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-500 font-mono-tech text-xs">…</div>;
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--monzal-bg)]">
        <div className="text-center">
          <h1 className="font-display text-3xl text-white">{t("auth.welcome")}</h1>
          <p className="text-gray-400 mt-3">{t("auth.welcomeSub")}</p>
          <button onClick={signIn} className="btn-gold mt-8" data-testid="admin-signin">{t("auth.signIn")}</button>
        </div>
      </div>
    );
  }
  if (!user.is_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--monzal-bg)]" data-testid="admin-forbidden">
        <div className="text-center max-w-md px-6">
          <h1 className="font-display text-2xl text-white">Access denied · Admin only</h1>
          <p className="text-gray-400 mt-3 text-sm">Tu cuenta no tiene privilegios de administrador.</p>
        </div>
      </div>
    );
  }

  const StatBox = ({ label, value }) => (
    <div className="card-luxury p-6">
      <div className="overline">{label}</div>
      <div className="font-display text-3xl text-[var(--monzal-gold)] mt-2">{value}</div>
    </div>
  );

  return (
    <div data-testid="admin-page" className="bg-[var(--monzal-bg)] min-h-screen">
      <div className="border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-12 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <div className="overline">ADMIN</div>
            <h1 className="font-display font-light text-3xl lg:text-5xl text-white tracking-tight mt-3">{t("admin.title")}</h1>
            <p className="text-gray-400 mt-2">{t("admin.sub")}</p>
          </div>
          <button onClick={handleReseed} className="btn-ghost" data-testid="admin-reseed">{t("admin.seed")}</button>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-10">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-12" data-testid="admin-stats">
            <StatBox label={t("admin.reptiles")} value={stats.reptiles} />
            <StatBox label={t("admin.products")} value={stats.products} />
            <StatBox label={t("admin.breeders")} value={stats.breeders} />
            <StatBox label={t("admin.users")} value={stats.users} />
            <StatBox label={t("admin.orders")} value={stats.orders} />
            <StatBox label={t("admin.revenue")} value={`$${stats.revenue}`} />
          </div>
        )}

        <div className="flex gap-2 mb-8">
          {["reptile", "product", "listings"].map((tk) => (
            <button
              key={tk}
              data-testid={`admin-tab-${tk}`}
              onClick={() => setTab(tk)}
              className={`text-xs uppercase tracking-[0.22em] font-display px-4 py-2.5 border transition ${tab === tk ? "border-[var(--monzal-gold)] text-[var(--monzal-gold)]" : "border-white/10 text-gray-400"}`}
            >
              {tk === "reptile" ? t("admin.newReptile") : tk === "product" ? t("admin.newProduct") : t("admin.listings")}
            </button>
          ))}
        </div>

        {tab === "reptile" && (
          <div className="card-luxury p-6 lg:p-10" data-testid="admin-new-reptile-form">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <Field label={t("admin.title_field")} value={repForm.title} onChange={(v) => setRepForm({ ...repForm, title: v })} testid="rep-title" />
              <div>
                <label className="overline block mb-2">{t("admin.species_field")}</label>
                <select value={repForm.species} onChange={(e) => setRepForm({ ...repForm, species: e.target.value })} className="w-full bg-black border border-white/10 px-3 py-2.5 text-white font-mono-tech text-sm" data-testid="rep-species">
                  <option>Ball Python</option>
                  <option>Boa Constrictor</option>
                  <option>Leopard Gecko</option>
                  <option>Crested Gecko</option>
                  <option>Gargoyle Gecko</option>
                  <option>Reticulated Python</option>
                  <option>Carpet Python</option>
                </select>
              </div>
              <Field label={t("admin.morph_field")} value={repForm.morph} onChange={(v) => setRepForm({ ...repForm, morph: v })} testid="rep-morph" />
              <Field label={t("admin.price_field")} type="number" value={repForm.price} onChange={(v) => setRepForm({ ...repForm, price: v })} testid="rep-price" />
              <div>
                <label className="overline block mb-2">{t("admin.sex_field")}</label>
                <select value={repForm.sex} onChange={(e) => setRepForm({ ...repForm, sex: e.target.value })} className="w-full bg-black border border-white/10 px-3 py-2.5 text-white font-mono-tech text-sm" data-testid="rep-sex">
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="unsexed">Unsexed</option>
                </select>
              </div>
              <Field label={t("admin.weight_field")} type="number" value={repForm.weight_g} onChange={(v) => setRepForm({ ...repForm, weight_g: v })} testid="rep-weight" />
              <Field label={t("admin.hatch_field")} type="date" value={repForm.hatch_date} onChange={(v) => setRepForm({ ...repForm, hatch_date: v })} testid="rep-hatch" />
              <Field label="Age (months)" type="number" value={repForm.age_months} onChange={(v) => setRepForm({ ...repForm, age_months: v })} testid="rep-age" />
              <Field label={t("admin.genetics_field")} value={repForm.genetics} onChange={(v) => setRepForm({ ...repForm, genetics: v })} testid="rep-genetics" />
              <Field label={t("admin.hets_field")} value={repForm.hets} onChange={(v) => setRepForm({ ...repForm, hets: v })} testid="rep-hets" />
              <div>
                <label className="overline block mb-2">{t("admin.breeder_field")}</label>
                <select value={repForm.breeder_id} onChange={(e) => setRepForm({ ...repForm, breeder_id: e.target.value })} className="w-full bg-black border border-white/10 px-3 py-2.5 text-white font-mono-tech text-sm" data-testid="rep-breeder">
                  <option value="">Select…</option>
                  {breeders.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-3">
                <label className="overline">{t("admin.featured_field")}</label>
                <input type="checkbox" checked={repForm.featured} onChange={(e) => setRepForm({ ...repForm, featured: e.target.checked })} data-testid="rep-featured" />
              </div>
              <Field label={t("admin.cover_field")} value={repForm.cover} onChange={(v) => setRepForm({ ...repForm, cover: v })} testid="rep-cover" />
              <Field label={t("admin.gallery_field")} value={repForm.gallery} onChange={(v) => setRepForm({ ...repForm, gallery: v })} testid="rep-gallery" />
              <div className="lg:col-span-2">
                <label className="overline block mb-2">{t("admin.notes_field")}</label>
                <textarea value={repForm.breeder_notes} onChange={(e) => setRepForm({ ...repForm, breeder_notes: e.target.value })} rows={3} className="w-full bg-black border border-white/10 px-3 py-2.5 text-white font-body text-sm" data-testid="rep-notes" />
              </div>
            </div>
            <div className="mt-8 flex items-center gap-4 flex-wrap">
              <label className="btn-ghost cursor-pointer">
                <Upload size={14} /> {uploading ? "…" : t("admin.uploadImage")}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUpload(e, "reptile")} data-testid="rep-upload-input" />
              </label>
              <button onClick={handleSaveReptile} disabled={saving} className="btn-emerald-glow" data-testid="rep-save-btn">
                {saving ? t("admin.saving") : t("admin.save")}
              </button>
            </div>
          </div>
        )}

        {tab === "product" && (
          <div className="card-luxury p-6 lg:p-10" data-testid="admin-new-product-form">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <Field label={t("admin.title_field")} value={prodForm.title} onChange={(v) => setProdForm({ ...prodForm, title: v })} testid="prd-title" />
              <div>
                <label className="overline block mb-2">{t("admin.category_field")}</label>
                <select value={prodForm.category} onChange={(e) => setProdForm({ ...prodForm, category: e.target.value })} className="w-full bg-black border border-white/10 px-3 py-2.5 text-white font-mono-tech text-sm" data-testid="prd-category">
                  <option value="accessories">Accessories</option>
                  <option value="enclosure">Enclosure</option>
                  <option value="food">Food</option>
                  <option value="merch">Merch</option>
                </select>
              </div>
              <Field label={t("admin.price_field")} type="number" value={prodForm.price} onChange={(v) => setProdForm({ ...prodForm, price: v })} testid="prd-price" />
              <Field label={t("admin.stock_field")} type="number" value={prodForm.stock} onChange={(v) => setProdForm({ ...prodForm, stock: v })} testid="prd-stock" />
              <Field label={t("admin.cover_field")} value={prodForm.cover} onChange={(v) => setProdForm({ ...prodForm, cover: v })} testid="prd-cover" />
              <div className="flex items-center gap-3">
                <label className="overline">{t("admin.featured_field")}</label>
                <input type="checkbox" checked={prodForm.featured} onChange={(e) => setProdForm({ ...prodForm, featured: e.target.checked })} data-testid="prd-featured" />
              </div>
              <div className="lg:col-span-2">
                <label className="overline block mb-2">{t("admin.description_field")}</label>
                <textarea value={prodForm.description} onChange={(e) => setProdForm({ ...prodForm, description: e.target.value })} rows={3} className="w-full bg-black border border-white/10 px-3 py-2.5 text-white font-body text-sm" data-testid="prd-desc" />
              </div>
            </div>
            <div className="mt-8 flex items-center gap-4 flex-wrap">
              <label className="btn-ghost cursor-pointer">
                <Upload size={14} /> {uploading ? "…" : t("admin.uploadImage")}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => handleUpload(e, "product")} data-testid="prd-upload-input" />
              </label>
              <button onClick={handleSaveProduct} disabled={saving} className="btn-emerald-glow" data-testid="prd-save-btn">
                {saving ? t("admin.saving") : t("admin.save")}
              </button>
            </div>
          </div>
        )}

        {tab === "listings" && (
          <div className="card-luxury overflow-hidden" data-testid="admin-listings">
            <table className="w-full text-sm">
              <thead className="bg-black/50">
                <tr className="text-left">
                  <th className="px-4 py-3 overline">Title</th>
                  <th className="px-4 py-3 overline">Species</th>
                  <th className="px-4 py-3 overline">Price</th>
                  <th className="px-4 py-3 overline">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {listings.map((r) => (
                  <tr key={r.id} className="border-t border-white/5">
                    <td className="px-4 py-3 text-white">{r.title}</td>
                    <td className="px-4 py-3 text-gray-400 font-mono-tech text-xs">{r.species}</td>
                    <td className="px-4 py-3 text-[var(--monzal-gold)]">${r.price}</td>
                    <td className="px-4 py-3 text-gray-400 font-mono-tech text-xs">{r.status}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => handleDelete(r.id)} data-testid={`admin-delete-${r.id}`} className="text-gray-500 hover:text-red-400">
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, type = "text", testid }) => (
  <div>
    <label className="overline block mb-2">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full bg-black border border-white/10 px-3 py-2.5 text-white font-mono-tech text-sm focus:border-[var(--monzal-gold)] outline-none"
    />
  </div>
);

export default Admin;
